import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  CheckCircle2,
  AlertTriangle,
  HelpCircle,
  ListOrdered,
  FileText,
  Download,
  Compass,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronRight,
  Printer
} from 'lucide-react';
import { SALES_ENABLED } from '../../lib/featureFlags';

/**
 * BeslutslagePremiumGeneric - Reusable Premium Content Display
 * 
 * Works with any calculator by accepting generated premium content.
 * Use the premiumContentGenerators to create the content object.
 */

const BeslutslagePremiumGeneric = ({ 
  content,
  calculatorName = 'Kalkylator',
  onDownloadPDF,
  onPrint
}) => {
  // Wartemodus: never render Premium content in public UI while sales are paused.
  if (!SALES_ENABLED) return null;
  if (!content) return null;
  
  const { lagesbild, osakerheter, beslutsbehov, nastaSteg, beslutsbrev } = content;

  const ImpactBadge = ({ impact, direction }) => {
    const colors = {
      hög: 'bg-red-100 text-red-700 border-red-200',
      medel: 'bg-amber-100 text-amber-700 border-amber-200',
      låg: 'bg-green-100 text-green-700 border-green-200'
    };
    const DirectionIcon = direction === 'upp' ? TrendingUp : direction === 'ned' ? TrendingDown : Minus;
    
    return (
      <div className="flex items-center gap-2">
        <span className={`px-2 py-0.5 text-xs font-medium rounded border ${colors[impact]}`}>
          {impact}
        </span>
        <DirectionIcon className="h-3 w-3 text-gray-400" />
      </div>
    );
  };

  const AnswerBadge = ({ answer }) => {
    const config = {
      ja: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50' },
      nej: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
      delvis: { icon: HelpCircle, color: 'text-amber-600', bg: 'bg-amber-50' }
    };
    const { icon: Icon, color, bg } = config[answer] || config.delvis;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${color} ${bg}`}>
        <Icon className="h-3 w-3" />
        {answer.charAt(0).toUpperCase() + answer.slice(1)}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="beslutslage-premium-content">
      {/* Premium Header */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-forest-600 to-forest-700 rounded-lg text-white">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-6 w-6" />
          <div>
            <h2 className="text-lg font-semibold">Beslutsläge aktiverat</h2>
            <p className="text-forest-100 text-sm">Fördjupat beslutsunderlag</p>
          </div>
        </div>
        <span className="px-3 py-1 bg-white/20 rounded-full text-sm">
          Premium
        </span>
      </div>

      {/* 1. LÄGESBILD */}
      {lagesbild && (
        <Card className="border-forest-200" data-testid="premium-lagesbild">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-forest-800">
              <Compass className="h-5 w-5" />
              1. {lagesbild.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700 leading-relaxed">
              {lagesbild.summary}
            </p>
            <div className="grid grid-cols-2 gap-3">
              {lagesbild.keyPoints?.map((point, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-gray-600 bg-forest-50 p-2 rounded">
                  <ChevronRight className="h-4 w-4 text-forest-500" />
                  {point}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 2. OSÄKERHETER */}
      {osakerheter && (
        <Card className="border-amber-200" data-testid="premium-osakerheter">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-5 w-5" />
              2. {osakerheter.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">{osakerheter.intro}</p>
            <div className="space-y-3">
              {osakerheter.factors?.map((item, i) => (
                <div key={i} className="p-3 bg-amber-50/50 rounded-lg border border-amber-100">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-800">{item.factor}</span>
                    <ImpactBadge impact={item.impact} direction={item.direction} />
                  </div>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 3. BESLUTSBEHOV */}
      {beslutsbehov && (
        <Card className="border-blue-200" data-testid="premium-beslutsbehov">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <HelpCircle className="h-5 w-5" />
              3. {beslutsbehov.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="font-medium text-gray-800">{beslutsbehov.question}</p>
            <div className="space-y-2">
              {beslutsbehov.assessments?.map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <AnswerBadge answer={item.answer} />
                  <div className="flex-1">
                    <p className="font-medium text-gray-800 text-sm">{item.label}</p>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-sm text-blue-700 italic border-l-2 border-blue-300 pl-3 py-1 bg-blue-50/50 rounded-r">
              {beslutsbehov.recommendation}
            </p>
          </CardContent>
        </Card>
      )}

      {/* 4. NÄSTA STEG */}
      {nastaSteg && (
        <Card className="border-purple-200" data-testid="premium-nasta-steg">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-purple-800">
              <ListOrdered className="h-5 w-5" />
              4. {nastaSteg.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">{nastaSteg.intro}</p>
            <div className="space-y-3">
              {nastaSteg.steps?.map((step, i) => (
                <div key={i} className="flex gap-3 p-3 bg-purple-50/50 rounded-lg border border-purple-100">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-purple-600 text-white text-sm flex items-center justify-center font-semibold">
                    {step.priority}
                  </span>
                  <div className="flex-1">
                    <p className="font-medium text-purple-800">{step.action}</p>
                    <p className="text-sm text-gray-600 mt-0.5">{step.how}</p>
                    <p className="text-xs text-gray-500 mt-1 italic">{step.why}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 5. BESLUTSBREV / SAMMANFATTNING */}
      {beslutsbrev && (
        <Card className="border-2 border-forest-300 bg-gradient-to-br from-forest-50 to-white" data-testid="premium-beslutsbrev">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-forest-800">
              <FileText className="h-5 w-5" />
              5. {beslutsbrev.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Decision Letter Content */}
            <div className="p-4 bg-white rounded-lg border border-forest-200 space-y-3 print:border-none" id="beslutsbrev-content">
              <div className="flex justify-between items-start border-b border-gray-100 pb-2">
                <div>
                  <p className="font-semibold text-forest-800">Skogskalkyl Beslutsbrev</p>
                  <p className="text-xs text-gray-500">Genererat {beslutsbrev.date}</p>
                </div>
                <span className="text-xs px-2 py-0.5 bg-forest-100 text-forest-700 rounded">
                  Beslutsläge Premium
                </span>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-[120px_1fr] gap-2">
                  <span className="text-gray-500 font-medium">Ärende:</span>
                  <span className="text-gray-800">{beslutsbrev.sections?.arende}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-2">
                  <span className="text-gray-500 font-medium">Resultat:</span>
                  <span className="text-gray-800">{beslutsbrev.sections?.resultat}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-2">
                  <span className="text-gray-500 font-medium">Position:</span>
                  <span className="text-gray-800">{beslutsbrev.sections?.position}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-2">
                  <span className="text-gray-500 font-medium">Osäkerheter:</span>
                  <span className="text-gray-800">{beslutsbrev.sections?.huvudosakerheter}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-2">
                  <span className="text-gray-500 font-medium">Bedömning:</span>
                  <span className="text-gray-800">{beslutsbrev.sections?.beslutslage}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-2">
                  <span className="text-gray-500 font-medium">Nästa steg:</span>
                  <span className="text-gray-800 font-medium">{beslutsbrev.sections?.prioriteradAtgard}</span>
                </div>
              </div>
              
              <div className="pt-2 border-t border-gray-100 text-xs text-gray-400">
                Skogskalkyl.se · Från siffra till bättre beslut
              </div>
            </div>

            {/* Export Buttons */}
            <div className="flex gap-2 pt-2">
              <Button 
                onClick={onDownloadPDF}
                className="flex-1 bg-forest-700 hover:bg-forest-800 text-white"
                data-testid="download-pdf-btn"
              >
                <Download className="mr-2 h-4 w-4" />
                Ladda ner PDF
              </Button>
              <Button 
                onClick={onPrint}
                variant="outline"
                className="border-forest-300 text-forest-700 hover:bg-forest-50"
                data-testid="print-btn"
              >
                <Printer className="mr-2 h-4 w-4" />
                Skriv ut
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BeslutslagePremiumGeneric;
